import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';
import {
  Add,
  CalendarMonth,
  Check,
  CheckBoxOutlined,
  CheckCircle,
  ExpandLess,
  ExpandMore,
  FavoriteBorder,
} from '@mui/icons-material';
import clsx from 'clsx';
import {
  AVAILABLE_TODO_INTEGRATIONS_TYPE,
  CalendarPreference,
  TodoIntegrationType,
} from '@demind-inc/core';
import { debounce, uniqBy, upperFirst } from 'lodash';
import { isValidElementType } from 'react-is';
import { useTranslation } from 'react-i18next';

import {
  useAuthContext,
  useCalendarContext,
  useUpdateCalendarPref,
  useWearablesContext,
  useTerraWidget,
} from '../../../data-access';
import { useConnectCalendar, useStartTodoAuth, useConnectAppleReminders } from '../../../hooks';
import { CalendarAccountDropdown } from '../../CalendarView';
import { AppSwitch } from '../../common';
import { WEARABLES, MobileSdkOnlyWearbales } from '../../SideBar/constants';
import {
  isAppleCalendarDesktopSupported,
  isAppleRemindersDesktopSupported,
} from '../../../helpers';
import { useFeatureFlag } from '../../../utils';
import GoogleIcon from '../../../assets/calendar/google.png';
import OutlookIcon from '../../../assets/calendar/outlook.png';
import AppleCalIcon from '../../../assets/calendar/apple.png';
import { TODO_INTEGRATION_ICONS } from '../../TodoIntegration';

const CALENDAR_TYPE_ICONS: Record<string, string> = {
  google: GoogleIcon,
  outlook: OutlookIcon,
  apple: AppleCalIcon,
};

type UnSyncToCalendarType = NonNullable<CalendarPreference['unSyncToCalendar']>;

type ExternalCalendarSyncOption = {
  key: keyof UnSyncToCalendarType;
  labelKey: string;
  descriptionKey?: string;
};

const DEFAULT_UNSYNC_TO_CALENDAR: UnSyncToCalendarType = {
  aiScheduler: false,
  dragAndDrop: false,
  task: false,
  routine: false,
  meal: false,
  sleep: false,
};

const EXTERNAL_CALENDAR_SYNC_OPTIONS: ExternalCalendarSyncOption[] = [
  {
    key: 'aiScheduler',
    labelKey: 'settings.calendar.aiGeneratedEvents',
    descriptionKey: 'settings.calendar.externalCalendarSyncAiTooltip',
  },
  {
    key: 'dragAndDrop',
    labelKey: 'settings.calendar.dragAndDrop',
    descriptionKey: 'settings.calendar.externalCalendarSyncDragTooltip',
  },
  {
    key: 'task',
    labelKey: 'settings.calendar.autoScheduledTask',
    descriptionKey: 'settings.calendar.externalCalendarSyncTaskTooltip',
  },
  {
    key: 'routine',
    labelKey: 'settings.calendar.autoScheduledRoutine',
    descriptionKey: 'settings.calendar.externalCalendarSyncRoutineTooltip',
  },
  {
    key: 'meal',
    labelKey: 'settings.aiAssistant.planTypes.meal',
    descriptionKey: 'settings.calendar.externalCalendarSyncMealTooltip',
  },
  {
    key: 'sleep',
    labelKey: 'settings.aiAssistant.planTypes.sleep',
    descriptionKey: 'settings.calendar.externalCalendarSyncSleepTooltip',
  },
];

type ConnSection = 'calendar' | 'todos' | 'health' | null;

const normalizeWearableId = (manufacturer?: string): string => {
  if (!manufacturer) return '';
  const n = manufacturer.toUpperCase();
  return n === 'APPLE_HEALTH' ? 'APPLE' : n;
};

const getTodoDisplayName = (t: TodoIntegrationType): string => {
  if (t === 'github') return 'GitHub';
  if (t === 'clickup') return 'ClickUp';
  if (t === 'google-tasks') return 'Google Tasks';
  if (t === 'apple-reminders') return 'Apple Reminders';
  return upperFirst(t);
};

/** Section toggle: dashed ring when disconnected, purple filled + check when connected */
const ConnectionSectionCheckbox: React.FC<{ connected: boolean }> = ({ connected }) => (
  <span
    role="img"
    aria-label={connected ? 'Connected' : 'Not connected'}
    className={clsx(
      'power-up-plan-page__conn-check',
      connected && 'power-up-plan-page__conn-check--connected'
    )}
  >
    {connected ? <Check className="power-up-plan-page__conn-check__tick" aria-hidden /> : null}
  </span>
);

/** Small connected badge anchored on top of each integration icon in the list */
const ConnectionRowCheckbox: React.FC = () => (
  <span
    aria-hidden
    className={clsx(
      'power-up-plan-page__conn-check',
      'power-up-plan-page__conn-check--connected',
      'power-up-plan-page__conn-check--on-icon'
    )}
  >
    <Check className="power-up-plan-page__conn-check__tick" />
  </span>
);

export interface ConnectionsState {
  hasCalendar: boolean;
  hasTodos: boolean;
  hasHealth: boolean;
}

export interface PowerUpPlanConnectionsProps {
  redirectTo: string;
  onConnectionsChange: (state: ConnectionsState) => void;
}

export const PowerUpPlanConnections: React.FC<PowerUpPlanConnectionsProps> = ({
  redirectTo,
  onConnectionsChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { calendarsMeta, calendarAccountsToCreateEvents, calendarPreferences, mainCalendar } =
    useCalendarContext();
  const { updateCalendarPref } = useUpdateCalendarPref();
  const { connectCal, isLoading: isConnectingCal } = useConnectCalendar();
  const { featureFlags } = useFeatureFlag();
  const { allDevices } = useWearablesContext();
  const { terraWidget, isPending: isConnectingWearable } = useTerraWidget();
  const { connectedTodos, generatingTodoAuth, handleOpenTodoAuth } = useStartTodoAuth({
    redirectTo,
  });
  const { connectAppleReminders, isLoading: isConnectingAppleReminders } =
    useConnectAppleReminders();

  const isAppleCalDesktopSupported = isAppleCalendarDesktopSupported();
  const isAppleRemindersSupported = isAppleRemindersDesktopSupported();

  const [openConn, setOpenConn] = useState<ConnSection>(null);
  const [connectingCalType, setConnectingCalType] = useState<'google' | 'outlook' | 'apple' | null>(
    null
  );
  const [calMenuAnchor, setCalMenuAnchor] = useState<HTMLElement | null>(null);
  const [todoMenuAnchor, setTodoMenuAnchor] = useState<HTMLElement | null>(null);
  const [healthMenuAnchor, setHealthMenuAnchor] = useState<HTMLElement | null>(null);
  const [defaultCalendarId, setDefaultCalendarId] = useState('');
  const [unSyncToCalendar, setUnSyncToCalendar] = useState<UnSyncToCalendarType>(
    DEFAULT_UNSYNC_TO_CALENDAR
  );
  const [openExternalSync, setOpenExternalSync] = useState(false);
  const pendingCalendarPrefsRef = useRef<Partial<CalendarPreference>>({});
  const unSyncToCalendarRef = useRef(unSyncToCalendar);

  useEffect(() => {
    unSyncToCalendarRef.current = unSyncToCalendar;
  }, [unSyncToCalendar]);

  useEffect(() => {
    if (Object.keys(pendingCalendarPrefsRef.current).length > 0) {
      return;
    }

    setDefaultCalendarId(calendarPreferences.defaultCalendarId || mainCalendar?.calendarId || '');
  }, [calendarPreferences.defaultCalendarId, mainCalendar?.calendarId]);

  useEffect(() => {
    if (Object.keys(pendingCalendarPrefsRef.current).length > 0) {
      return;
    }

    setUnSyncToCalendar({
      ...DEFAULT_UNSYNC_TO_CALENDAR,
      ...(calendarPreferences.unSyncToCalendar || {}),
    });
  }, [calendarPreferences.unSyncToCalendar]);

  const debouncedUpdateCalendarSettings = useMemo(
    () =>
      debounce(() => {
        if (!user.preferenceId) {
          return;
        }

        const preferences = pendingCalendarPrefsRef.current;
        pendingCalendarPrefsRef.current = {};

        if (Object.keys(preferences).length === 0) {
          return;
        }

        updateCalendarPref({
          preferenceId: user.preferenceId,
          preferences,
        });
      }, 1000),
    [updateCalendarPref, user.preferenceId]
  );

  const queueCalendarSettingsUpdate = useCallback(
    (newData: Partial<CalendarPreference>) => {
      pendingCalendarPrefsRef.current = {
        ...pendingCalendarPrefsRef.current,
        ...newData,
        ...(newData.unSyncToCalendar
          ? {
              unSyncToCalendar: {
                ...pendingCalendarPrefsRef.current.unSyncToCalendar,
                ...newData.unSyncToCalendar,
              },
            }
          : {}),
      };
      debouncedUpdateCalendarSettings();
    },
    [debouncedUpdateCalendarSettings]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateCalendarSettings.flush();
    };
  }, [debouncedUpdateCalendarSettings]);

  const handleDefaultCalendarChange = useCallback(
    (calendarId: string) => {
      setDefaultCalendarId(calendarId);
      queueCalendarSettingsUpdate({
        defaultCalendarId: calendarId,
      });
    },
    [queueCalendarSettingsUpdate]
  );

  const handleExternalCalendarSyncToggle = useCallback(
    (key: keyof UnSyncToCalendarType, checked: boolean) => {
      const updated = {
        ...DEFAULT_UNSYNC_TO_CALENDAR,
        ...unSyncToCalendarRef.current,
        [key]: !checked,
      };

      unSyncToCalendarRef.current = updated;
      setUnSyncToCalendar(updated);
      queueCalendarSettingsUpdate({
        unSyncToCalendar: updated,
      });
    },
    [queueCalendarSettingsUpdate]
  );

  const toggleConn = (section: Exclude<ConnSection, null>) =>
    setOpenConn((prev) => (prev === section ? null : section));

  const connectedCalendarAccounts = useMemo(() => {
    const external = (calendarAccountsToCreateEvents ?? calendarsMeta ?? []).filter(
      (c) => c.calendarType && c.calendarType !== 'lifestack'
    );
    return uniqBy(external, (c) => c.rootEmail);
  }, [calendarAccountsToCreateEvents, calendarsMeta]);

  const hasConnectedExternalCalendars = useMemo(
    () =>
      (calendarAccountsToCreateEvents ?? []).some(
        (calendar) => calendar.calendarType && calendar.calendarType !== 'lifestack'
      ),
    [calendarAccountsToCreateEvents]
  );

  const connectedTodoAccounts = useMemo(
    () => uniqBy(connectedTodos ?? [], (t) => t.source),
    [connectedTodos]
  );

  const connectedHealthSources = useMemo(() => {
    return uniqBy(
      (allDevices ?? []).map((device) => ({
        ...device,
        normalizedManufacturer: normalizeWearableId(device.terraManufacturer),
      })),
      (d) => d.normalizedManufacturer
    );
  }, [allDevices]);

  const availableTodoTypes = useMemo(
    () =>
      AVAILABLE_TODO_INTEGRATIONS_TYPE.filter(
        (t) =>
          (t !== 'github' || featureFlags.FF_github_integrations) &&
          (t !== 'notion' || featureFlags.FF_notion_integ) &&
          (t !== 'google-tasks' || featureFlags.FF_google_tasks) &&
          (t !== 'apple-reminders' || isAppleRemindersSupported)
      ),
    [featureFlags, isAppleRemindersSupported]
  );

  const hasCalendar = connectedCalendarAccounts.length > 0;
  const hasTodos = connectedTodoAccounts.length > 0;
  const hasHealth = connectedHealthSources.length > 0;

  useEffect(() => {
    onConnectionsChange({ hasCalendar, hasTodos, hasHealth });
  }, [hasCalendar, hasTodos, hasHealth]);

  const handleConnectCalendar = async (type: 'google' | 'outlook' | 'apple') => {
    setCalMenuAnchor(null);
    setConnectingCalType(type);
    await connectCal(type, redirectTo);
    setConnectingCalType(null);
  };

  const handleConnectTodo = async (todoType: TodoIntegrationType) => {
    if (generatingTodoAuth || isConnectingAppleReminders) return;
    setTodoMenuAnchor(null);
    if (todoType === 'apple-reminders' && isAppleRemindersSupported) {
      await connectAppleReminders();
      return;
    }
    await handleOpenTodoAuth(todoType);
  };

  const handleConnectWearable = () => {
    setHealthMenuAnchor(null);
    terraWidget({ metricId: user.metricId, redirectTo, sourceType: 'ALL' });
  };

  const isTodoConnected = (t: TodoIntegrationType) =>
    connectedTodos?.some((todo) => todo.source === t);

  return (
    <>
      {/* Calendar */}
      <div className="quick-pref-page__section">
        <button
          type="button"
          className="quick-pref-page__section-header"
          onClick={() => toggleConn('calendar')}
        >
          <ConnectionSectionCheckbox connected={hasCalendar} />
          <CalendarMonth style={{ fontSize: 20, color: '#7c3aed' }} />
          <Typography variant="subtitle1" className="quick-pref-page__section-title">
            Calendars
          </Typography>
          {openConn === 'calendar' ? <ExpandLess /> : <ExpandMore />}
        </button>
        {openConn === 'calendar' && (
          <div className="quick-pref-page__section-body">
            <Typography variant="caption" className="power-up-plan-page__calendar-note">
              You can use Lifestack without connecting your calendars
            </Typography>
            {hasCalendar && (
              <div className="connect-world-page__connected-list">
                {connectedCalendarAccounts.map((cal) => (
                  <div key={cal.calendarId} className="connect-world-page__connected-row">
                    <div className="power-up-plan-page__conn-item-icon">
                      {cal.calendarType && CALENDAR_TYPE_ICONS[cal.calendarType] ? (
                        <img
                          src={CALENDAR_TYPE_ICONS[cal.calendarType!]}
                          alt={cal.calendarType}
                          className="connect-world-page__item-icon"
                        />
                      ) : (
                        <CheckCircle className="connect-world-page__item-dot" />
                      )}
                      <ConnectionRowCheckbox />
                    </div>
                    <Typography variant="body2" className="connect-world-page__item-label">
                      {cal.rootEmail}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
            {hasConnectedExternalCalendars && (
              <>
                <div className="power-up-plan-page__default-calendar">
                  <Typography
                    variant="subtitle2"
                    className="power-up-plan-page__default-calendar-title"
                  >
                    Default calendar
                  </Typography>
                  <Typography
                    variant="caption"
                    className="power-up-plan-page__default-calendar-desc"
                  >
                    Choose where new events and todos should be scheduled.
                  </Typography>
                  <CalendarAccountDropdown
                    calendars={calendarAccountsToCreateEvents}
                    onSelectCalendarId={handleDefaultCalendarChange}
                    selectedCalendarId={defaultCalendarId}
                    className="power-up-plan-page__default-calendar-dropdown"
                  />
                </div>
                <div className="power-up-plan-page__external-sync">
                  <button
                    type="button"
                    className="power-up-plan-page__external-sync-header"
                    onClick={() => setOpenExternalSync((prev) => !prev)}
                    aria-expanded={openExternalSync}
                  >
                    <div className="power-up-plan-page__external-sync-header-copy">
                      <Typography
                        variant="subtitle2"
                        className="power-up-plan-page__external-sync-title"
                      >
                        {t('settings.calendar.externalCalendarSync')}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="power-up-plan-page__external-sync-desc"
                      >
                        {t('settings.calendar.externalCalendarSyncDescription')}
                      </Typography>
                    </div>
                    {openExternalSync ? <ExpandLess /> : <ExpandMore />}
                  </button>
                  {openExternalSync && (
                    <div className="power-up-plan-page__external-sync-list">
                      {EXTERNAL_CALENDAR_SYNC_OPTIONS.map((option) => (
                        <div key={option.key} className="power-up-plan-page__external-sync-row">
                          <div className="power-up-plan-page__external-sync-copy">
                            <Typography
                              variant="body2"
                              className="power-up-plan-page__external-sync-label"
                            >
                              {t(option.labelKey)}
                            </Typography>
                            {option.descriptionKey && (
                              <Typography
                                variant="caption"
                                className="power-up-plan-page__external-sync-option-desc"
                              >
                                {t(option.descriptionKey)}
                              </Typography>
                            )}
                          </div>
                          <AppSwitch
                            checked={!unSyncToCalendar[option.key]}
                            onChange={(event) =>
                              handleExternalCalendarSyncToggle(option.key, event.target.checked)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              type="button"
              className="connect-world-page__connect-btn"
              onClick={(e) => setCalMenuAnchor(e.currentTarget)}
            >
              <Add fontSize="inherit" /> Connect
            </button>
            <Menu
              anchorEl={calMenuAnchor}
              open={Boolean(calMenuAnchor)}
              onClose={() => setCalMenuAnchor(null)}
              slotProps={{ paper: { className: 'connect-world-page__menu-paper' } }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {(['google', 'outlook'] as const).map((type) => (
                <MenuItem
                  key={type}
                  onClick={() => handleConnectCalendar(type)}
                  disabled={isConnectingCal && connectingCalType === type}
                  className="connect-world-page__menu-item"
                >
                  <img
                    src={CALENDAR_TYPE_ICONS[type]}
                    alt={type}
                    className="connect-world-page__menu-icon"
                  />
                  <span className="connect-world-page__menu-item-title">
                    {type === 'google' ? 'Google Calendar' : 'Outlook Calendar'}
                  </span>
                  <span className="connect-world-page__menu-action">
                    {isConnectingCal && connectingCalType === type ? 'Connecting…' : 'Connect'}
                  </span>
                </MenuItem>
              ))}
              {isAppleCalDesktopSupported && (
                <MenuItem
                  onClick={() => handleConnectCalendar('apple')}
                  disabled={isConnectingCal}
                  className="connect-world-page__menu-item"
                >
                  <img
                    src={CALENDAR_TYPE_ICONS.apple}
                    alt="apple"
                    className="connect-world-page__menu-icon"
                  />
                  <span className="connect-world-page__menu-item-title">Apple Calendar</span>
                  <span className="connect-world-page__menu-action">
                    {isConnectingCal && connectingCalType === 'apple' ? 'Connecting…' : 'Connect'}
                  </span>
                </MenuItem>
              )}
              <div className="connect-world-page__menu-caption">
                * You can connect multiple accounts
              </div>
            </Menu>
          </div>
        )}
      </div>

      {/* Todos */}
      <div className="quick-pref-page__section">
        <button
          type="button"
          className="quick-pref-page__section-header"
          onClick={() => toggleConn('todos')}
        >
          <ConnectionSectionCheckbox connected={hasTodos} />
          <CheckBoxOutlined style={{ fontSize: 20, color: '#7c3aed' }} />
          <Typography variant="subtitle1" className="quick-pref-page__section-title">
            Todos
          </Typography>
          {openConn === 'todos' ? <ExpandLess /> : <ExpandMore />}
        </button>
        {openConn === 'todos' && (
          <div className="quick-pref-page__section-body">
            {connectedTodoAccounts.length > 0 && (
              <div className="connect-world-page__connected-list">
                {connectedTodoAccounts.map((todo) => (
                  <div key={todo.source} className="connect-world-page__connected-row">
                    <div className="power-up-plan-page__conn-item-icon">
                      {TODO_INTEGRATION_ICONS[todo.source] ? (
                        <img
                          src={TODO_INTEGRATION_ICONS[todo.source]}
                          alt={todo.source}
                          className="connect-world-page__item-icon"
                        />
                      ) : (
                        <CheckCircle className="connect-world-page__item-dot" />
                      )}
                      <ConnectionRowCheckbox />
                    </div>
                    <Typography variant="body2" className="connect-world-page__item-label">
                      {getTodoDisplayName(todo.source)}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              className="connect-world-page__connect-btn"
              onClick={(e) => setTodoMenuAnchor(e.currentTarget)}
            >
              <Add fontSize="inherit" /> Connect
            </button>
            <Menu
              anchorEl={todoMenuAnchor}
              open={Boolean(todoMenuAnchor)}
              onClose={() => setTodoMenuAnchor(null)}
              slotProps={{ paper: { className: 'connect-world-page__menu-paper' } }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {availableTodoTypes.map((todoType) => {
                const connected = isTodoConnected(todoType);
                const connecting = generatingTodoAuth === todoType;
                return (
                  <MenuItem
                    key={todoType}
                    onClick={() => !connected && handleConnectTodo(todoType)}
                    disabled={!!generatingTodoAuth && !connecting}
                    className="connect-world-page__menu-item"
                  >
                    {TODO_INTEGRATION_ICONS[todoType] && (
                      <img
                        src={TODO_INTEGRATION_ICONS[todoType]}
                        alt={todoType}
                        className="connect-world-page__menu-icon"
                      />
                    )}
                    <span className="connect-world-page__menu-item-title">
                      {getTodoDisplayName(todoType)}
                    </span>
                    <span
                      className={`connect-world-page__menu-action${connected ? ' connect-world-page__menu-action--connected' : ''}`}
                    >
                      {connecting ? 'Connecting…' : connected ? 'Connected' : 'Connect'}
                    </span>
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
        )}
      </div>

      {/* Health */}
      <div className="quick-pref-page__section">
        <button
          type="button"
          className="quick-pref-page__section-header"
          onClick={() => toggleConn('health')}
        >
          <ConnectionSectionCheckbox connected={hasHealth} />
          <FavoriteBorder style={{ fontSize: 20, color: '#7c3aed' }} />
          <Typography variant="subtitle1" className="quick-pref-page__section-title">
            Health
          </Typography>
          {openConn === 'health' ? <ExpandLess /> : <ExpandMore />}
        </button>
        {openConn === 'health' && (
          <div className="quick-pref-page__section-body">
            {connectedHealthSources.length > 0 && (
              <div className="connect-world-page__connected-list">
                {connectedHealthSources.map((device) => {
                  const wearable = WEARABLES.find((w) => w.id === device.normalizedManufacturer);
                  const WearableIcon = wearable?.Icon;
                  return (
                    <div
                      key={device.normalizedManufacturer}
                      className="connect-world-page__connected-row"
                    >
                      <div className="power-up-plan-page__conn-item-icon">
                        {isValidElementType(WearableIcon) ? (
                          <WearableIcon className="connect-world-page__item-icon-svg" />
                        ) : (
                          <CheckCircle className="connect-world-page__item-dot" />
                        )}
                        <ConnectionRowCheckbox />
                      </div>
                      <Typography variant="body2" className="connect-world-page__item-label">
                        {wearable?.name ?? device.terraManufacturer}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              className="connect-world-page__connect-btn"
              onClick={(e) => setHealthMenuAnchor(e.currentTarget)}
            >
              <Add fontSize="inherit" /> Connect
            </button>
            <Menu
              anchorEl={healthMenuAnchor}
              open={Boolean(healthMenuAnchor)}
              onClose={() => setHealthMenuAnchor(null)}
              slotProps={{ paper: { className: 'connect-world-page__menu-paper' } }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {WEARABLES.map(({ id, name, Icon }) => {
                const connected = connectedHealthSources.some(
                  (d) => d.normalizedManufacturer === id
                );
                const mobileOnly = MobileSdkOnlyWearbales.includes(id as any);
                return (
                  <MenuItem
                    key={id}
                    onClick={() => !connected && !mobileOnly && handleConnectWearable()}
                    disabled={mobileOnly || isConnectingWearable}
                    className="connect-world-page__menu-item"
                  >
                    {isValidElementType(Icon) ? (
                      <Icon className="connect-world-page__menu-icon-svg" />
                    ) : (
                      <CheckCircle className="connect-world-page__menu-icon-svg" />
                    )}
                    <span className="connect-world-page__menu-item-title">{name}</span>
                    <span
                      className={`connect-world-page__menu-action${connected || mobileOnly ? ' connect-world-page__menu-action--connected' : ''}`}
                    >
                      {mobileOnly ? 'Mobile only' : connected ? 'Connected' : 'Connect'}
                    </span>
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
        )}
      </div>
    </>
  );
};
